import { useEffect, useState } from "react";
import { Users, KeyRound, Globe, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ClanLogo } from "./ClanLogo";
import { LogoPicker, DEFAULT_LOGO } from "./LogoPicker";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

export function CreateClan({ onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [logo, setLogo] = useState(DEFAULT_LOGO);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/clans", { name, description, public: isPublic, logo });
      toast.success(`Clan "${data.name}" created`);
      onCreated(data);
    } catch (err) {
      toast.error(errMsg(err, "Could not create clan"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="panel p-6" data-testid="create-clan-form">
      <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-4">// FOUND A CLAN</div>
      <label className="block text-[10px] tracking-widest text-muted-foreground mb-1">NAME</label>
      <input value={name} onChange={(e) => setName(e.target.value)} minLength={3} maxLength={32} required data-testid="clan-name-input"
        placeholder="Night Shift Legends" className="w-full bg-transparent border border-phosphor/30 focus:border-phosphor outline-none px-4 py-3 text-sm font-mono mb-4" />
      <label className="block text-[10px] tracking-widest text-muted-foreground mb-1">DESCRIPTION</label>
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={240} rows={2} data-testid="clan-desc-input"
        placeholder="Who you are and what you're drilling" className="w-full bg-transparent border border-phosphor/30 focus:border-phosphor outline-none px-4 py-3 text-sm mb-4" />
      <div className="mb-5">
        <label className="block text-[10px] tracking-widest text-muted-foreground mb-2">LOGO</label>
        <LogoPicker logo={logo} onChange={setLogo} />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-6">
        <button type="button" onClick={() => setIsPublic(false)} data-testid="clan-private-btn"
          className={`text-left px-4 py-3 border ${!isPublic ? "border-phosphor bg-primary/10 text-phosphor" : "border-border"}`}>
          <div className="text-xs font-semibold tracking-widest flex items-center gap-2"><Lock className="w-3 h-3" /> INVITE ONLY</div>
          <div className="text-[11px] text-muted-foreground mt-1">Members join with your invite code.</div>
        </button>
        <button type="button" onClick={() => setIsPublic(true)} data-testid="clan-public-btn"
          className={`text-left px-4 py-3 border ${isPublic ? "border-phosphor bg-primary/10 text-phosphor" : "border-border"}`}>
          <div className="text-xs font-semibold tracking-widest flex items-center gap-2"><Globe className="w-3 h-3" /> PUBLIC</div>
          <div className="text-[11px] text-muted-foreground mt-1">Anyone can find and join.</div>
        </button>
      </div>
      <button type="submit" disabled={busy || name.trim().length < 3} data-testid="create-clan-submit"
        className="bg-primary text-primary-foreground px-6 py-3 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-primary/90 disabled:opacity-50">
        {busy ? "Creating…" : "Create clan"}
      </button>
    </form>
  );
}

export function JoinClan({ onJoined }) {
  const [code, setCode] = useState("");
  const [clans, setClans] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => api.get("/clans/public", { params: { q } }).then((r) => setClans(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);

  const joinCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/clans/join", { code: code.trim() });
      toast.success(`Welcome to ${data.name}`);
      onJoined(data);
    } catch (err) {
      toast.error(errMsg(err, "Could not join"));
    } finally {
      setBusy(false);
    }
  };

  const joinPublic = async (clan) => {
    try {
      const { data } = await api.post(`/clans/${clan.id}/join`);
      toast.success(`Welcome to ${data.name}`);
      onJoined(data);
    } catch (err) {
      toast.error(errMsg(err, "Could not join"));
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={joinCode} className="panel p-6" data-testid="join-code-form">
        <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-4">// JOIN WITH INVITE CODE</div>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} data-testid="invite-code-input"
              placeholder="A1B2C3" className="w-full bg-transparent border border-phosphor/30 focus:border-phosphor outline-none pl-10 pr-4 py-3 text-sm font-mono tracking-[0.3em]" />
          </div>
          <button type="submit" disabled={busy || code.trim().length < 4} data-testid="invite-code-submit"
            className="bg-primary text-primary-foreground px-6 py-3 text-xs tracking-[0.25em] font-semibold hover:bg-primary/90 disabled:opacity-50">JOIN</button>
        </div>
      </form>

      <div className="panel p-6" data-testid="public-clans">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] tracking-[0.3em] text-muted-foreground">// PUBLIC CLANS</div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="public-search"
            className="bg-transparent border border-border focus:border-phosphor outline-none px-3 py-1.5 text-xs font-mono w-40" />
        </div>
        {clans.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="public-empty">No public clans yet — be the first to found one.</div>
        ) : (
          <div className="space-y-2">
            {clans.map((c) => (
              <div key={c.id} className="flex items-center gap-4 border border-border p-3" data-testid={`public-clan-${c.id}`}>
                <ClanLogo logo={c.logo} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{c.description || "—"}</div>
                  {c.challenge && <div className="text-[10px] tracking-widest text-phosphor mt-1">{c.challenge.label.toUpperCase()} · {c.challenge.period.toUpperCase()}</div>}
                </div>
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.member_count}/25</div>
                <button onClick={() => joinPublic(c)} disabled={c.member_count >= 25} data-testid={`join-public-${c.id}`}
                  className="border border-phosphor/50 px-4 py-2 text-[10px] tracking-widest hover:bg-primary/10 disabled:opacity-40">
                  {c.member_count >= 25 ? "FULL" : "JOIN"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
